// SCR-STAFF-701 (AP-MED-02؛ US-MED-034): صف خطای ارسال ملی با علت، شمار تلاش و ارسال دوباره. خطای موقت خودکار دوباره ارسال
// می‌شود (BR-MED-035) و این صفحه فقط خطاهای نیازمند اقدام کاربر و ردیف‌های در انتظار اعتبارنامه را برای پیگیری نشان می‌دهد.
// نمای عملیاتی است: بدون محتوای بالینی، پس پذیرش هم آن را می‌بیند (C-AGENT-871).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { unwrap, type MedNationalSubmissionView } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, faDigits, ListPage, toJalaliDateTime, useDisplaySettings, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { NationalStatusChip, nationalKeys, reasonLabelKey, SCR_STAFF_701, useNationalQueue } from './national';

type Filter = 'all' | 'Failed' | 'AwaitingProviderAction' | 'Pending';

export function NationalQueuePage() {
  const { t } = useTranslation('med');
  const { tenantApi, session } = useTenantSession();
  const { timeZone } = useDisplaySettings();
  const access = useAccess();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [filter, setFilter] = useState<Filter>('Failed');
  const rows = useNationalQueue(filter);
  const canManage = access.can('med.national_submission.manage');

  const resend = useMutation({
    mutationFn: (id: string) =>
      unwrap(
        tenantApi.POST('/api/v1/medical-orders/national-submissions/{id}:resend', {
          params: { path: { id } },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => {
      idempotency.settle(error);
      void queryClient.invalidateQueries({ queryKey: nationalKeys.queue(session?.tenantId, filter) });
    },
  });

  const columns: DataTableColumn<MedNationalSubmissionView>[] = [
    { id: 'status', headerKey: 'med:national.status', cell: (row) => <NationalStatusChip status={row.status} /> },
    { id: 'insurer', headerKey: 'med:national.insurer', cell: (row) => <span>{row.insurer}</span> },
    { id: 'type', headerKey: 'med:national.presc_type', cell: (row) => <span>{faDigits(row.prescriptionTypeId)}</span> },
    {
      id: 'reason',
      headerKey: 'med:national.error',
      cell: (row) => (
        <span data-testid={`med-queue-reason-${row.id}`}>
          {row.errorCode ? `${row.errorCode} · ${t(reasonLabelKey(row.failureReason))}` : t(reasonLabelKey(row.failureReason))}
        </span>
      ),
    },
    {
      id: 'attempts',
      headerKey: 'med:national.attempts',
      cell: (row) => <span data-testid={`med-queue-attempts-${row.id}`}>{faDigits(row.attempts)}</span>,
    },
    {
      id: 'created',
      headerKey: 'med:national.created_at',
      cell: (row) => <span>{toJalaliDateTime(row.createdAt, { timeZone })}</span>,
    },
    {
      id: 'actions',
      headerKey: 'med:national.resend',
      cell: (row) =>
        canManage && row.status !== 'Registered' && row.status !== 'Deleted' ? (
          <Button size="small" variant="outlined" onClick={() => resend.mutate(row.id)} data-testid={`med-queue-resend-${row.id}`}>
            {t('med:national.resend')}
          </Button>
        ) : null,
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_701}
      titleKey="med:national.queue_title"
      subtitleKey="med:national.queue_subtitle"
      filters={
        <TextField
          select
          size="small"
          label={t('med:national.status')}
          value={filter}
          onChange={(event) => setFilter(event.target.value as Filter)}
          data-testid="med-queue-filter"
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="all">{t('med:national.filter_all')}</MenuItem>
          <MenuItem value="Failed">{t('med:national_status.Failed')}</MenuItem>
          <MenuItem value="AwaitingProviderAction">{t('med:national_status.AwaitingProviderAction')}</MenuItem>
          <MenuItem value="Pending">{t('med:national_status.Pending')}</MenuItem>
        </TextField>
      }
    >
      <Stack spacing={2}>
        {(rows.data ?? []).some((row) => row.status === 'AwaitingProviderAction') ? (
          <Alert severity="warning" data-testid="med-queue-awaiting-hint">
            {t('med:national.no_session')}
          </Alert>
        ) : null}
        <DataTable
          rows={rows.data}
          columns={columns}
          getRowId={(row) => row.id}
          isLoading={rows.isLoading}
          error={rows.error}
          onRetry={() => void rows.refetch()}
          emptyTitleKey="med:national.queue_empty"
          captionKey="med:national.queue_title"
        />
      </Stack>
    </ListPage>
  );
}
